import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { Plus } from "lucide-react";
import { api } from "@/lib/api";
import { useQuery, invalidate } from "@/lib/useQuery";
import type { AccountListItem } from "@/lib/types";
import { stageLabel, sourceLabel, STAGES, SOURCES, US_STATES, shortDate } from "@/lib/format";
import { Button, PageHeader, Loading, Empty, Modal, Field, Dot, Pill, toast } from "@/components/ui";
import { TerritoryWarningModal } from "@/pages/account/TerritoryWarning";

export function AccountsPage() {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("");
  const [creating, setCreating] = useState(false);
  const { data, loading } = useQuery<AccountListItem[]>("accounts", () => api.get<AccountListItem[]>("/accounts"));
  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (data ?? []).filter((a) => (!stage || a.stage === stage) && (!s || a.name.toLowerCase().includes(s) || a.number.toLowerCase().includes(s) || (a.primaryContact?.name ?? "").toLowerCase().includes(s)));
  }, [data, q, stage]);

  return (
    <>
      <PageHeader title="Accounts" sub={data ? `${data.length}` : undefined}>
        <input className="input" style={{ width: 220 }} placeholder="Filter" value={q} onChange={(e) => setQ(e.target.value)} />
        <select className="select" style={{ width: 160 }} value={stage} onChange={(e) => setStage(e.target.value)}>
          <option value="">All stages</option>
          {STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <Button variant="primary" onClick={() => setCreating(true)}><Plus size={14} /> New account</Button>
      </PageHeader>
      <div className="page">
        <div className="card table-wrap">
          {loading && !data ? <Loading /> : rows.length === 0 ? <Empty>No accounts yet. Create the first one.</Empty> : (
            <table className="table">
              <thead>
                <tr><th>Account</th><th>Number</th><th>Stage</th><th>Status</th><th>Primary contact</th><th>Source</th><th className="desktop-only">Assigned</th><th className="desktop-only">Updated</th></tr>
              </thead>
              <tbody>
                {rows.map((a) => {
                  const worst = a.systems.find((s) => s.status === "red") ?? a.systems.find((s) => s.status === "yellow") ?? a.systems.find((s) => s.live);
                  return (
                    <tr key={a.id} className="click" onClick={() => nav(`/accounts/${a.id}`)}>
                      <td style={{ fontWeight: 500 }}>{a.name}</td>
                      <td className="num muted">{a.number}</td>
                      <td><Pill>{stageLabel(a.stage)}</Pill></td>
                      <td>{worst ? <span className="row gap-6"><Dot color={worst.status as "red"} pulse={worst.status === "red"} /><span className="small muted-2">{worst.name}</span></span> : <span className="dim">—</span>}</td>
                      <td className="muted-2">{a.primaryContact?.name ?? <span className="dim">—</span>}</td>
                      <td className="muted-2">{a.partnerName ? `Partner · ${a.partnerName}` : sourceLabel(a.source)}</td>
                      <td className="muted-2 desktop-only">{a.assignedTo.join(", ") || <span className="dim">—</span>}</td>
                      <td className="muted small desktop-only">{shortDate(a.updatedAt)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {creating && <NewAccountModal onClose={() => setCreating(false)} onCreated={(id) => { setCreating(false); invalidate("accounts"); nav(`/accounts/${id}`); }} />}
    </>
  );
}

export function NewAccountModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [form, setForm] = useState({ name: "", businessType: "", solutionType: "", city: "", stateCode: "", source: "direct", erp: "", domain: "", partnerId: "" });
  const [busy, setBusy] = useState(false);
  const [warning, setWarning] = useState<{ accountId: string; territoryId: string; partnerName: string; stateCode: string } | null>(null);
  const { data: partners } = useQuery<{ id: string; name: string }[]>("partners-brief", () => api.get<{ id: string; name: string }[]>("/partners?brief=1").catch(() => []));
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async () => {
    if (!form.name.trim()) return;
    setBusy(true);
    try {
      const res = await api.post<{ id: string; territoryWarning: { territoryId: string; partnerName: string; stateCode: string } | null }>("/accounts", {
        ...form, partnerId: form.partnerId || null, stateCode: form.stateCode || null, domain: form.domain || null, erp: form.erp || null, businessType: form.businessType || null, solutionType: form.solutionType || null, city: form.city || null,
      });
      if (res.territoryWarning) setWarning({ accountId: res.id, ...res.territoryWarning });
      else onCreated(res.id);
    } catch (e) { toast((e as Error).message, true); } finally { setBusy(false); }
  };
  if (warning) return <TerritoryWarningModal accountId={warning.accountId} territoryId={warning.territoryId} partnerName={warning.partnerName} stateCode={warning.stateCode} onDone={() => onCreated(warning.accountId)} />;
  return (
    <Modal title="New account" onClose={onClose} footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" disabled={busy || !form.name.trim()} onClick={submit}>Create account</Button></>}>
      <Field label="Company name"><input className="input" autoFocus value={form.name} onChange={set("name")} /></Field>
      <div className="form-grid">
        <Field label="Business type"><input className="input" placeholder="Metal fabrication" value={form.businessType} onChange={set("businessType")} /></Field>
        <Field label="ERP"><input className="input" placeholder="JobBOSS²" value={form.erp} onChange={set("erp")} /></Field>
        <Field label="City"><input className="input" value={form.city} onChange={set("city")} /></Field>
        <Field label="State"><select className="select" value={form.stateCode} onChange={set("stateCode")}><option value="">—</option>{US_STATES.map((s) => <option key={s}>{s}</option>)}</select></Field>
        <Field label="Source"><select className="select" value={form.source} onChange={set("source")}>{SOURCES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}</select></Field>
        <Field label="Sourcing partner"><select className="select" value={form.partnerId} onChange={set("partnerId")}><option value="">None</option>{(partners ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Field>
        <Field label="Email domain" hint="Used to auto-link inbound mail"><input className="input" placeholder="ridgelinefab.com" value={form.domain} onChange={set("domain")} /></Field>
      </div>
      <Field label="Solution"><input className="input" placeholder="Shop floor scoreboard, work ticket queue" value={form.solutionType} onChange={set("solutionType")} /></Field>
    </Modal>
  );
}
